import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY);

const CITIES = ['Miami', 'LA', 'Mexico City', 'Barcelona', 'Madrid', 'Bogota', 'Buenos Aires'];
const CATEGORIES = ['restaurant', 'cafe', 'place_to_visit', 'other'];

const CATEGORY_ICON = { restaurant: '🍽️', cafe: '☕', place_to_visit: '📍', other: '✨' };

const MEDIA_TYPES = ['movie', 'podcast', 'book'];
const MEDIA_LEVELS = ['Beginner', 'Intermediate', 'All'];
const MEDIA_ICON = { movie: '🎬', podcast: '🎧', book: '📚' };

const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉'];

const STR = {
  en: {
    back: 'Back', community: 'Community', chatTab: '💬 Group Chat', recTab: '🌎 Recommendations',
    allCities: 'All cities', selectCity: 'City', selectCategory: 'Category', allCategories: 'All',
    catLabels: { restaurant: 'Restaurants', cafe: 'Cafes', place_to_visit: 'Places to visit', other: 'Other' },
    newPost: '+ New recommendation', postCity: 'City', postCategory: 'Category', postTitle: 'Title',
    postTitlePh: 'E.g. Best tacos in Roma Norte', postContent: 'Your recommendation',
    postContentPh: 'Tell others why you recommend it...', submit: 'Submit for review', submitting: 'Sending...',
    submitted: '✓ Sent! A teacher will review it before it goes live.', cancel: 'Cancel',
    noPosts: 'No approved recommendations yet for this filter.', noPostsSub: 'Be the first to share one!',
    chatPh: 'Write a message...', send: 'Send', chatEmpty: 'No messages yet. Say hi! 👋', you: 'You',
    chatBanner: 'This is the group chat for the whole Idiomas Quevedo community — it\'s yours to use! Say hi, share tips, ask questions, whatever helps. The chat resets every Sunday at midnight (Brasília time).',
    guidelines: 'Posts are reviewed by a teacher before appearing here.',
    close: 'Close',
    mediaTab: '🎬 Media',
    mediaTypeLabels: { movie: 'Movies & Shows', podcast: 'Podcasts', book: 'Books' },
    allTypes: 'All types', allLevels: 'All levels', level: 'Level',
    newMedia: '+ Suggest a title', mediaType: 'Type', mediaTitle: 'Title', mediaTitlePh: 'E.g. Coco (2017)',
    mediaDesc: 'Why do you recommend it?', mediaDescPh: 'What makes it good for learning Spanish?',
    mediaLink: 'Link (optional)', mediaLinkPh: 'https://...',
    noMedia: 'No approved titles yet for this filter.',
    everyone: 'Everyone', myCohort: 'My Cohort', cohortUnknown: "We couldn't find your join month yet.",
    winsTab: '🏆 Wins', newWin: '+ Share a win', winPh: "What went well this week? A conversation you had, something you understood, anything counts...",
    noWins: 'No wins shared yet this week.', noWinsSub: 'Be the first — small wins count too!',
    speakingTab: '🎙️ Practice Calls',
    speakingIntro: "Post when you're free for a quick 10-minute speaking practice call with another student — no pressure, just practice.",
    newCall: '+ Offer a time', callPh: "E.g. Free Tuesday around 6pm, 10 min?", callSubmit: 'Post it',
    noCalls: 'No open practice calls right now.', noCallsSub: 'Be the first to offer a time!',
    imIn: "I'm in 🙌", matched: '✓ Matched with', matchedWith: 'You matched with', sayHiChat: 'Say hi in the Group Chat to set it up!',
    yourRequest: 'Your request', openStatus: 'Open — waiting for someone', callMatchedBanner: 'Matched! Go say hi in the Group Chat.',
    curatedBy: 'Curated by Idiomas Quevedo',
  },
  pt: {
    back: 'Voltar', community: 'Comunidade', chatTab: '💬 Chat em Grupo', recTab: '🌎 Recomendações',
    allCities: 'Todas as cidades', selectCity: 'Cidade', selectCategory: 'Categoria', allCategories: 'Todas',
    catLabels: { restaurant: 'Restaurantes', cafe: 'Cafés', place_to_visit: 'Lugares para visitar', other: 'Outros' },
    newPost: '+ Nova recomendação', postCity: 'Cidade', postCategory: 'Categoria', postTitle: 'Título',
    postTitlePh: 'Ex: Melhores tacos na Roma Norte', postContent: 'Sua recomendação',
    postContentPh: 'Conte por que você recomenda...', submit: 'Enviar para revisão', submitting: 'Enviando...',
    submitted: '✓ Enviado! Um professor vai revisar antes de publicar.', cancel: 'Cancelar',
    noPosts: 'Ainda não há recomendações aprovadas para esse filtro.', noPostsSub: 'Seja o primeiro a compartilhar!',
    chatPh: 'Escreva uma mensagem...', send: 'Enviar', chatEmpty: 'Nenhuma mensagem ainda. Diga oi! 👋', you: 'Você',
    chatBanner: 'Este é o chat em grupo de toda a comunidade Idiomas Quevedo — é seu, use à vontade! Diga oi, compartilhe dicas, tire dúvidas, o que for útil. O chat é reiniciado todo domingo à meia-noite (horário de Brasília).',
    guidelines: 'As postagens são revisadas por um professor antes de aparecer aqui.',
    close: 'Fechar',
    mediaTab: '🎬 Mídia',
    mediaTypeLabels: { movie: 'Filmes e Séries', podcast: 'Podcasts', book: 'Livros' },
    allTypes: 'Todos os tipos', allLevels: 'Todos os níveis', level: 'Nível',
    newMedia: '+ Sugerir um título', mediaType: 'Tipo', mediaTitle: 'Título', mediaTitlePh: 'Ex: Coco (2017)',
    mediaDesc: 'Por que você recomenda?', mediaDescPh: 'O que torna bom para aprender espanhol?',
    mediaLink: 'Link (opcional)', mediaLinkPh: 'https://...',
    noMedia: 'Ainda não há títulos aprovados para esse filtro.',
    everyone: 'Todos', myCohort: 'Minha Turma', cohortUnknown: 'Ainda não encontramos seu mês de entrada.',
    winsTab: '🏆 Conquistas', newWin: '+ Compartilhar uma conquista', winPh: 'O que deu certo esta semana? Uma conversa, algo que você entendeu, tudo conta...',
    noWins: 'Ainda não há conquistas compartilhadas esta semana.', noWinsSub: 'Seja o primeiro — pequenas conquistas contam também!',
    speakingTab: '🎙️ Chamadas de Prática',
    speakingIntro: 'Publique quando você estiver livre para uma chamada rápida de 10 minutos de prática com outro aluno — sem pressão, só prática.',
    newCall: '+ Oferecer um horário', callPh: 'Ex: Livre terça-feira por volta das 18h, 10 min?', callSubmit: 'Publicar',
    noCalls: 'Nenhuma chamada de prática aberta no momento.', noCallsSub: 'Seja o primeiro a oferecer um horário!',
    imIn: 'Topo 🙌', matched: '✓ Combinado com', matchedWith: 'Você combinou com', sayHiChat: 'Diga oi no Chat em Grupo para combinar!',
    yourRequest: 'Seu pedido', openStatus: 'Aberto — aguardando alguém', callMatchedBanner: 'Combinado! Vá dizer oi no Chat em Grupo.',
    curatedBy: 'Selecionado por Idiomas Quevedo',
  },
  es: {
    back: 'Volver', community: 'Comunidad', chatTab: '💬 Chat Grupal', recTab: '🌎 Recomendaciones',
    allCities: 'Todas las ciudades', selectCity: 'Ciudad', selectCategory: 'Categoría', allCategories: 'Todas',
    catLabels: { restaurant: 'Restaurantes', cafe: 'Cafés', place_to_visit: 'Lugares para visitar', other: 'Otros' },
    newPost: '+ Nueva recomendación', postCity: 'Ciudad', postCategory: 'Categoría', postTitle: 'Título',
    postTitlePh: 'Ej: Los mejores tacos en Roma Norte', postContent: 'Tu recomendación',
    postContentPh: 'Cuéntanos por qué la recomiendas...', submit: 'Enviar para revisión', submitting: 'Enviando...',
    submitted: '✓ ¡Enviado! Un profesor lo revisará antes de publicarlo.', cancel: 'Cancelar',
    noPosts: 'Todavía no hay recomendaciones aprobadas para este filtro.', noPostsSub: '¡Sé el primero en compartir una!',
    chatPh: 'Escribe un mensaje...', send: 'Enviar', chatEmpty: 'Aún no hay mensajes. ¡Saluda! 👋', you: 'Tú',
    chatBanner: '¡Este es el chat grupal de toda la comunidad de Idiomas Quevedo — es tuyo, úsalo con confianza! Saluda, comparte consejos, pregunta lo que necesites. El chat se reinicia cada domingo a medianoche (hora de Brasilia).',
    guidelines: 'Las publicaciones son revisadas por un profesor antes de aparecer aquí.',
    close: 'Cerrar',
    mediaTab: '🎬 Media',
    mediaTypeLabels: { movie: 'Películas y Series', podcast: 'Podcasts', book: 'Libros' },
    allTypes: 'Todos los tipos', allLevels: 'Todos los niveles', level: 'Nivel',
    newMedia: '+ Sugerir un título', mediaType: 'Tipo', mediaTitle: 'Título', mediaTitlePh: 'Ej: Coco (2017)',
    mediaDesc: '¿Por qué lo recomiendas?', mediaDescPh: '¿Qué lo hace bueno para aprender español?',
    mediaLink: 'Enlace (opcional)', mediaLinkPh: 'https://...',
    noMedia: 'Todavía no hay títulos aprobados para este filtro.',
    everyone: 'Todos', myCohort: 'Mi Cohorte', cohortUnknown: 'Todavía no encontramos tu mes de ingreso.',
    winsTab: '🏆 Logros', newWin: '+ Compartir un logro', winPh: '¿Qué salió bien esta semana? Una conversación, algo que entendiste, todo cuenta...',
    noWins: 'Todavía no hay logros compartidos esta semana.', noWinsSub: '¡Sé el primero — los pequeños logros también cuentan!',
    speakingTab: '🎙️ Llamadas de Práctica',
    speakingIntro: 'Publica cuando estés libre para una llamada rápida de 10 minutos de práctica con otro estudiante — sin presión, solo práctica.',
    newCall: '+ Ofrecer un horario', callPh: 'Ej: Libre el martes a las 18h, ¿10 min?', callSubmit: 'Publicar',
    noCalls: 'No hay llamadas de práctica abiertas ahora mismo.', noCallsSub: '¡Sé el primero en ofrecer un horario!',
    imIn: 'Me apunto 🙌', matched: '✓ Emparejado con', matchedWith: 'Te emparejaste con', sayHiChat: '¡Saluda en el Chat Grupal para coordinarlo!',
    yourRequest: 'Tu solicitud', openStatus: 'Abierto — esperando a alguien', callMatchedBanner: '¡Emparejado! Ve a saludar en el Chat Grupal.',
    curatedBy: 'Seleccionado por Idiomas Quevedo',
  },
};

export default function CommunityES({ session, lang, onBack }) {
  const S = STR[lang] || STR.es;
  const [tab, setTab] = useState('chat');

  return (
    <div className="app-wrap comm-page">
      <style>{communityStyles}</style>

      <button onClick={onBack} className="comm-back">← {S.back}</button>

      <div className="comm-hero">
        <div className="comm-hero-icon">🌎</div>
        <div>
          <h1 className="comm-title">{S.community}</h1>
          <p className="comm-hero-sub">
            {lang === 'es' ? 'Conecta, comparte y descubre con otros estudiantes' : lang === 'en' ? 'Connect, share and discover with fellow students' : 'Conecte-se, compartilhe e descubra com outros alunos'}
          </p>
        </div>
      </div>

      <div className="comm-tabs">
        <button className={`comm-tab ${tab === 'chat' ? 'active' : ''}`} onClick={() => setTab('chat')}>{S.chatTab}</button>
        <button className={`comm-tab ${tab === 'wins' ? 'active' : ''}`} onClick={() => setTab('wins')}>{S.winsTab}</button>
        <button className={`comm-tab ${tab === 'speaking' ? 'active' : ''}`} onClick={() => setTab('speaking')}>{S.speakingTab}</button>
        <button className={`comm-tab ${tab === 'recs' ? 'active' : ''}`} onClick={() => setTab('recs')}>{S.recTab}</button>
        <button className={`comm-tab ${tab === 'media' ? 'active' : ''}`} onClick={() => setTab('media')}>{S.mediaTab}</button>
      </div>

      {tab === 'chat' && <GroupChat session={session} S={S} lang={lang} />}
      {tab === 'wins' && <WinsFeed session={session} S={S} />}
      {tab === 'speaking' && <SpeakingCalls session={session} S={S} lang={lang} />}
      {tab === 'recs' && <Recommendations session={session} S={S} />}
      {tab === 'media' && <MediaRecs session={session} S={S} />}
    </div>
  );
}

// ============================================================
// GROUP CHAT
// ============================================================
function GroupChat({ session, S, lang }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [myCohort, setMyCohort] = useState(null); // 'YYYY-MM' or null if unknown
  const [cohortFilter, setCohortFilter] = useState('everyone'); // 'everyone' | 'mine'
  const [streaks, setStreaks] = useState({}); // student_id -> streak count
  const [reactions, setReactions] = useState({}); // message_id -> { emoji: { count, mine } }
  const bottomRef = useRef(null);

  const cohortKey = (dateStr) => (dateStr ? dateStr.slice(0, 7) : null); // 'YYYY-MM'

  useEffect(() => {
    supabase.from('students').select('contract_start, created_at').eq('id', session.id).single()
      .then(({ data }) => setMyCohort(cohortKey(data?.contract_start || data?.created_at)));
  }, [session.id]);

  const loadStreaks = useCallback(async (studentIds) => {
    if (!studentIds.length) return;
    const { data } = await supabase.from('game_completions').select('student_id, played_date').in('student_id', studentIds);
    const byStudent = {};
    (data || []).forEach(r => { (byStudent[r.student_id] = byStudent[r.student_id] || []).push(r.played_date); });
    const result = {};
    Object.entries(byStudent).forEach(([id, dates]) => {
      const uniqueDates = [...new Set(dates)].sort().reverse();
      const today = todayISOStr();
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      if (!uniqueDates.length || (uniqueDates[0] !== today && uniqueDates[0] !== yesterday)) { result[id] = 0; return; }
      let s = 1;
      for (let i = 1; i < uniqueDates.length; i++) {
        const diff = (new Date(uniqueDates[i - 1]) - new Date(uniqueDates[i])) / 86400000;
        if (diff === 1) s++; else break;
      }
      result[id] = s;
    });
    setStreaks(result);
  }, []);

  const loadReactions = useCallback(async (messageIds) => {
    if (!messageIds.length) return;
    const { data } = await supabase.from('community_message_reactions').select('message_id, student_id, emoji').in('message_id', messageIds);
    const grouped = {};
    (data || []).forEach(r => {
      grouped[r.message_id] = grouped[r.message_id] || {};
      grouped[r.message_id][r.emoji] = grouped[r.message_id][r.emoji] || { count: 0, mine: false };
      grouped[r.message_id][r.emoji].count += 1;
      if (r.student_id === session.id) grouped[r.message_id][r.emoji].mine = true;
    });
    setReactions(grouped);
  }, [session.id]);

  const loadMessages = useCallback(async () => {
    const { data } = await supabase
      .from('community_messages')
      .select('id, content, created_at, student_id, students(full_name, contract_start, created_at)')
      .order('created_at', { ascending: true })
      .limit(200);
    setMessages(data || []);
    setLoading(false);
    const ids = [...new Set((data || []).map(m => m.student_id))];
    loadStreaks(ids);
    loadReactions((data || []).map(m => m.id));
  }, [loadStreaks, loadReactions]);

  useEffect(() => {
    loadMessages();
    const channel = supabase
      .channel('community_messages_live')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'community_messages' }, (payload) => {
        setMessages(prev => [...prev, payload.new]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, cohortFilter]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const content = text.trim();
    setText('');
    const { error } = await supabase.from('community_messages').insert({ student_id: session.id, content });
    if (error) {
      setText(content);
    }
    setSending(false);
  };

  const toggleReaction = async (messageId, emoji) => {
    const current = reactions[messageId]?.[emoji];
    // Optimistic update
    setReactions(prev => {
      const next = { ...prev, [messageId]: { ...(prev[messageId] || {}) } };
      Object.keys(next[messageId]).forEach(e => {
        if (next[messageId][e].mine) next[messageId][e] = { ...next[messageId][e], count: next[messageId][e].count - 1, mine: false };
      });
      if (!current?.mine) {
        const prevCount = next[messageId][emoji]?.count || 0;
        next[messageId][emoji] = { count: prevCount + 1, mine: true };
      }
      return next;
    });
    // Remove any existing reaction from this user on this message, then add the new one (unless toggling off)
    await supabase.from('community_message_reactions').delete().eq('message_id', messageId).eq('student_id', session.id);
    if (!current?.mine) {
      await supabase.from('community_message_reactions').insert({ message_id: messageId, student_id: session.id, emoji });
    }
  };

  const nameFor = (m) => m.students?.full_name || (m.student_id === session.id ? session.full_name : '');

  const AVATAR_COLORS = ['#7c3aed', '#0284c7', '#16a34a', '#dc2626', '#d97706', '#db2777', '#0891b2'];
  const colorFor = (name) => {
    let hash = 0;
    for (let i = 0; i < (name || '?').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };
  const initialsFor = (name) => (name || '?').trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

  const locale = lang === 'es' ? 'es-ES' : lang === 'en' ? 'en-GB' : 'pt-BR';
  const stampFor = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    if (sameDay) return time;
    const day = d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
    return `${day} · ${time}`;
  };

  const visibleMessages = cohortFilter === 'mine' && myCohort
    ? messages.filter(m => cohortKey(m.students?.contract_start || m.students?.created_at) === myCohort || m.student_id === session.id)
    : messages;

  return (
    <div>
      <div className="comm-chat-banner">
        <span className="comm-chat-banner-icon">💬</span>
        <span>{S.chatBanner}</span>
      </div>

      {myCohort && (
        <div className="comm-cohort-toggle">
          <button className={`comm-cohort-btn ${cohortFilter === 'everyone' ? 'active' : ''}`} onClick={() => setCohortFilter('everyone')}>{S.everyone}</button>
          <button className={`comm-cohort-btn ${cohortFilter === 'mine' ? 'active' : ''}`} onClick={() => setCohortFilter('mine')}>{S.myCohort}</button>
        </div>
      )}

      <div className="comm-card comm-chat-card">
        <div className="comm-chat-scroll">
          {loading ? (
            <div className="comm-empty">…</div>
          ) : visibleMessages.length === 0 ? (
            <div className="comm-empty">{S.chatEmpty}</div>
          ) : (
            visibleMessages.map(m => {
              const mine = m.student_id === session.id;
              const name = mine ? S.you : nameFor(m);
              const streak = streaks[m.student_id] || 0;
              const msgReactions = reactions[m.id] || {};
              return (
                <div key={m.id} className={`comm-msg-row ${mine ? 'mine' : ''}`}>
                  {!mine && (
                    <div className="comm-avatar" style={{ background: colorFor(name) }}>{initialsFor(name)}</div>
                  )}
                  <div style={{ maxWidth: '72%' }}>
                    <div className={`comm-msg-bubble ${mine ? 'mine' : ''}`} style={{ maxWidth: '100%' }}>
                      <div className="comm-msg-meta">
                        <span className="comm-msg-name" style={{ color: mine ? 'rgba(255,255,255,0.85)' : colorFor(name) }}>{name}</span>
                        {streak >= 3 && <span className="comm-msg-streak">🔥{streak}</span>}
                        <span className="comm-msg-dot">·</span>
                        <span className="comm-msg-time">{stampFor(m.created_at)}</span>
                      </div>
                      <div className="comm-msg-text">{m.content}</div>
                    </div>
                    <div className={`comm-reaction-row ${mine ? 'mine' : ''}`}>
                      {REACTION_EMOJIS.map(emoji => {
                        const r = msgReactions[emoji];
                        return (
                          <button key={emoji} className={`comm-reaction-chip ${r?.mine ? 'active' : ''}`} onClick={() => toggleReaction(m.id, emoji)}>
                            {emoji}{r?.count > 0 && <span className="comm-reaction-count">{r.count}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
        <div className="comm-chat-input-row">
          <input
            className="comm-chat-input"
            placeholder={S.chatPh}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') send(); }}
            maxLength={500}
          />
          <button className="comm-chat-send" onClick={send} disabled={!text.trim() || sending}>{S.send}</button>
        </div>
      </div>
    </div>
  );
}

function todayISOStr() { return new Date().toISOString().split('T')[0]; }

// ============================================================
// RECOMMENDATIONS (moderated, city-filtered)
// ============================================================
function Recommendations({ session, S }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('community_posts').select('id, city, category, title, content, created_at, students!community_posts_student_id_fkey(full_name)').eq('status', 'approved').neq('category', 'win').order('created_at', { ascending: false });
    if (cityFilter !== 'all') q = q.eq('city', cityFilter);
    if (catFilter !== 'all') q = q.eq('category', catFilter);
    const { data } = await q;
    setPosts(data || []);
    setLoading(false);
  }, [cityFilter, catFilter]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  return (
    <div>
      <div className="comm-guidelines">ℹ️ {S.guidelines}</div>

      <div className="comm-filters">
        <select className="comm-select" value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
          <option value="all">{S.allCities}</option>
          {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="comm-select" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">{S.allCategories}</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_ICON[c]} {S.catLabels[c]}</option>)}
        </select>
      </div>

      <button className="comm-new-post-btn" onClick={() => setFormOpen(true)}>{S.newPost}</button>

      {loading ? (
        <div className="comm-empty">…</div>
      ) : posts.length === 0 ? (
        <div className="comm-empty-box">
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🗺️</div>
          <div style={{ fontWeight: '700' }}>{S.noPosts}</div>
          <div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#94a3b8' }}>{S.noPostsSub}</div>
        </div>
      ) : (
        <div className="comm-posts-list">
          {posts.map(p => (
            <div key={p.id} className="comm-post-card">
              <div className="comm-post-top">
                <span className="comm-post-cat">{CATEGORY_ICON[p.category]} {S.catLabels[p.category]}</span>
                <span className="comm-post-city">📍 {p.city}</span>
              </div>
              <div className="comm-post-title">{p.title}</div>
              <div className="comm-post-content">{p.content}</div>
              <div className="comm-post-author">— {p.students?.full_name || ''}</div>
            </div>
          ))}
        </div>
      )}

      {formOpen && <NewPostModal session={session} S={S} onClose={() => setFormOpen(false)} />}
    </div>
  );
}

function NewPostModal({ session, S, onClose }) {
  const [city, setCity] = useState(CITIES[0]);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('');

  const submit = async () => {
    if (!title.trim() || !content.trim()) return;
    setStatus('sending');
    const { error } = await supabase.from('community_posts').insert({
      student_id: session.id, city, category, title: title.trim(), content: content.trim(),
    });
    setStatus(error ? 'error' : 'done');
  };

  return (
    <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && onClose()}>
      <div className="modal-box modal-wide comm-modal" style={{ textAlign: 'left' }}>
        <button className="modal-close" onClick={onClose}>✕</button>
        {status === 'done' ? (
          <div className="comm-modal-success">
            <div className="comm-modal-success-icon">✅</div>
            <p style={{ fontWeight: '700', color: '#16a34a', fontSize: '0.92rem', lineHeight: 1.5 }}>{S.submitted}</p>
            <button onClick={onClose} className="comm-modal-submit" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white' }}>{S.close}</button>
          </div>
        ) : (
          <>
            <div className="comm-modal-icon-header">
              <div className="comm-modal-icon" style={{ background: '#f0fdf4' }}>🌎</div>
              <div className="comm-modal-title">{S.newPost}</div>
            </div>

            <label className="comm-form-label">{S.postCity}</label>
            <select className="comm-select comm-select-full" value={city} onChange={e => setCity(e.target.value)}>
              {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label className="comm-form-label">{S.postCategory}</label>
            <select className="comm-select comm-select-full" value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_ICON[c]} {S.catLabels[c]}</option>)}
            </select>

            <label className="comm-form-label">{S.postTitle}</label>
            <input className="comm-input-full" placeholder={S.postTitlePh} value={title} onChange={e => setTitle(e.target.value)} maxLength={100} />

            <label className="comm-form-label">{S.postContent}</label>
            <textarea className="comm-input-full comm-textarea" placeholder={S.postContentPh} value={content} onChange={e => setContent(e.target.value)} maxLength={600} rows={4} />

            {status === 'error' && <p style={{ color: '#dc2626', fontWeight: '700', fontSize: '0.85rem' }}>Error — try again.</p>}

            <button onClick={submit} disabled={!title.trim() || !content.trim() || status === 'sending'} className="comm-modal-submit" style={{ background: (title.trim() && content.trim()) ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#f1f5f9', color: (title.trim() && content.trim()) ? 'white' : '#94a3b8' }}>
              {status === 'sending' ? S.submitting : S.submit}
            </button>
            <button className="comm-modal-cancel" onClick={onClose}>{S.cancel}</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// WINS OF THE WEEK (moderated, reuses community_posts with category='win')
// ============================================================
function WinsFeed({ session, S }) {
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const loadWins = useCallback(async () => {
    setLoading(true);
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
    const { data } = await supabase
      .from('community_posts')
      .select('id, content, created_at, students!community_posts_student_id_fkey(full_name)')
      .eq('status', 'approved').eq('category', 'win').gte('created_at', weekAgo)
      .order('created_at', { ascending: false });
    setWins(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadWins(); }, [loadWins]);

  return (
    <div>
      <div className="comm-guidelines">ℹ️ {S.guidelines}</div>
      <button className="comm-new-post-btn comm-win-btn" onClick={() => setFormOpen(true)}>{S.newWin}</button>

      {loading ? (
        <div className="comm-empty">…</div>
      ) : wins.length === 0 ? (
        <div className="comm-empty-box">
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏆</div>
          <div style={{ fontWeight: '700' }}>{S.noWins}</div>
          <div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#94a3b8' }}>{S.noWinsSub}</div>
        </div>
      ) : (
        <div className="comm-posts-list">
          {wins.map(w => (
            <div key={w.id} className="comm-post-card comm-win-card">
              <div className="comm-post-content" style={{ fontSize: '0.92rem', fontWeight: '650' }}>🎉 {w.content}</div>
              <div className="comm-post-author">— {w.students?.full_name || ''}</div>
            </div>
          ))}
        </div>
      )}

      {formOpen && <NewWinModal session={session} S={S} onClose={() => setFormOpen(false)} />}
    </div>
  );
}

function NewWinModal({ session, S, onClose }) {
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('');

  const submit = async () => {
    if (!content.trim()) return;
    setStatus('sending');
    const { error } = await supabase.from('community_posts').insert({
      student_id: session.id, city: 'N/A', category: 'win', title: 'Win of the week', content: content.trim(),
    });
    setStatus(error ? 'error' : 'done');
  };

  return (
    <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && onClose()}>
      <div className="modal-box modal-wide comm-modal" style={{ textAlign: 'left' }}>
        <button className="modal-close" onClick={onClose}>✕</button>
        {status === 'done' ? (
          <div className="comm-modal-success">
            <div className="comm-modal-success-icon">✅</div>
            <p style={{ fontWeight: '700', color: '#16a34a', fontSize: '0.92rem', lineHeight: 1.5 }}>{S.submitted}</p>
            <button onClick={onClose} className="comm-modal-submit" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white' }}>{S.close}</button>
          </div>
        ) : (
          <>
            <div className="comm-modal-icon-header">
              <div className="comm-modal-icon" style={{ background: '#fffbeb' }}>🏆</div>
              <div className="comm-modal-title">{S.newWin}</div>
            </div>

            <textarea className="comm-input-full comm-textarea" placeholder={S.winPh} value={content} onChange={e => setContent(e.target.value)} maxLength={400} rows={4} autoFocus />

            {status === 'error' && <p style={{ color: '#dc2626', fontWeight: '700', fontSize: '0.85rem' }}>Error — try again.</p>}

            <button onClick={submit} disabled={!content.trim() || status === 'sending'} className="comm-modal-submit" style={{ background: content.trim() ? 'linear-gradient(135deg,#d97706,#b45309)' : '#f1f5f9', color: content.trim() ? 'white' : '#94a3b8' }}>
              {status === 'sending' ? S.submitting : S.submit}
            </button>
            <button className="comm-modal-cancel" onClick={onClose}>{S.cancel}</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SPEAKING PRACTICE CALLS (post availability, match with another student)
// ============================================================
function SpeakingCalls({ session, S, lang }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const loadCalls = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('community_speaking_calls')
      .select('id, student_id, note, status, matched_with, created_at, students!community_speaking_calls_student_id_fkey(full_name), matched_student:students!community_speaking_calls_matched_with_fkey(full_name)')
      .order('created_at', { ascending: false })
      .limit(50);
    setCalls(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadCalls(); }, [loadCalls]);

  const claim = async (call) => {
    await supabase.from('community_speaking_calls').update({ status: 'matched', matched_with: session.id }).eq('id', call.id).eq('status', 'open');
    loadCalls();
  };

  const locale = lang === 'es' ? 'es-ES' : lang === 'en' ? 'en-GB' : 'pt-BR';
  const dateFor = (iso) => new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short' });

  return (
    <div>
      <div className="comm-guidelines">ℹ️ {S.speakingIntro}</div>
      <button className="comm-new-post-btn comm-speaking-btn" onClick={() => setFormOpen(true)}>{S.newCall}</button>

      {loading ? (
        <div className="comm-empty">…</div>
      ) : calls.length === 0 ? (
        <div className="comm-empty-box">
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎙️</div>
          <div style={{ fontWeight: '700' }}>{S.noCalls}</div>
          <div style={{ fontSize: '0.8rem', marginTop: '4px', color: '#94a3b8' }}>{S.noCallsSub}</div>
        </div>
      ) : (
        <div className="comm-posts-list">
          {calls.map(c => {
            const isMine = c.student_id === session.id;
            const matched = c.status === 'matched';
            return (
              <div key={c.id} className="comm-post-card comm-call-card">
                <div className="comm-post-top">
                  <span className="comm-post-cat" style={{ color: '#0f766e', background: '#f0fdfa' }}>🎙️ {c.students?.full_name || ''}{isMine ? ` (${S.yourRequest})` : ''}</span>
                  <span className="comm-post-city">{dateFor(c.created_at)}</span>
                </div>
                <div className="comm-post-content">{c.note}</div>
                {matched ? (
                  <div className="comm-call-matched-banner">
                    {isMine
                      ? `${S.matched} ${c.matched_student?.full_name || ''} — ${S.sayHiChat}`
                      : (c.matched_with === session.id ? `${S.matchedWith} ${c.students?.full_name || ''} — ${S.sayHiChat}` : S.callMatchedBanner)}
                  </div>
                ) : isMine ? (
                  <div className="comm-call-open-badge">{S.openStatus}</div>
                ) : (
                  <button className="comm-call-join-btn" onClick={() => claim(c)}>{S.imIn}</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {formOpen && <NewCallModal session={session} S={S} onClose={() => { setFormOpen(false); loadCalls(); }} />}
    </div>
  );
}

function NewCallModal({ session, S, onClose }) {
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');

  const submit = async () => {
    if (!note.trim()) return;
    setStatus('sending');
    const { error } = await supabase.from('community_speaking_calls').insert({ student_id: session.id, note: note.trim(), status: 'open' });
    setStatus(error ? 'error' : 'done');
  };

  return (
    <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && onClose()}>
      <div className="modal-box modal-wide comm-modal" style={{ textAlign: 'left' }}>
        <button className="modal-close" onClick={onClose}>✕</button>
        {status === 'done' ? (
          <div className="comm-modal-success">
            <div className="comm-modal-success-icon">✅</div>
            <p style={{ fontWeight: '700', color: '#16a34a', fontSize: '0.92rem', lineHeight: 1.5 }}>{S.callSubmit} ✓</p>
            <button onClick={onClose} className="comm-modal-submit" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white' }}>{S.close}</button>
          </div>
        ) : (
          <>
            <div className="comm-modal-icon-header">
              <div className="comm-modal-icon" style={{ background: '#f0fdfa' }}>🎙️</div>
              <div className="comm-modal-title">{S.newCall}</div>
            </div>

            <textarea className="comm-input-full comm-textarea" placeholder={S.callPh} value={note} onChange={e => setNote(e.target.value)} maxLength={200} rows={3} autoFocus />

            {status === 'error' && <p style={{ color: '#dc2626', fontWeight: '700', fontSize: '0.85rem' }}>Error — try again.</p>}

            <button onClick={submit} disabled={!note.trim() || status === 'sending'} className="comm-modal-submit" style={{ background: note.trim() ? 'linear-gradient(135deg,#0f766e,#0d9488)' : '#f1f5f9', color: note.trim() ? 'white' : '#94a3b8' }}>
              {status === 'sending' ? S.submitting : S.callSubmit}
            </button>
            <button className="comm-modal-cancel" onClick={onClose}>{S.cancel}</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MEDIA (movies, podcasts, books) — curated + student-submitted
// ============================================================
function MediaRecs({ session, S }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('community_media').select('id, media_type, title, level, description, link, curated_by, created_at, students!community_media_student_id_fkey(full_name)').eq('status', 'approved').order('created_at', { ascending: false });
    if (typeFilter !== 'all') q = q.eq('media_type', typeFilter);
    if (levelFilter !== 'all') q = q.eq('level', levelFilter);
    const { data } = await q;
    setItems(data || []);
    setLoading(false);
  }, [typeFilter, levelFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  return (
    <div>
      <div className="comm-guidelines">ℹ️ {S.guidelines}</div>

      <div className="comm-filters">
        <select className="comm-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">{S.allTypes}</option>
          {MEDIA_TYPES.map(t => <option key={t} value={t}>{MEDIA_ICON[t]} {S.mediaTypeLabels[t]}</option>)}
        </select>
        <select className="comm-select" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
          <option value="all">{S.allLevels}</option>
          {MEDIA_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>

      <button className="comm-new-post-btn comm-media-btn" onClick={() => setFormOpen(true)}>{S.newMedia}</button>

      {loading ? (
        <div className="comm-empty">…</div>
      ) : items.length === 0 ? (
        <div className="comm-empty-box">
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🎬</div>
          <div style={{ fontWeight: '700' }}>{S.noMedia}</div>
        </div>
      ) : (
        <div className="comm-posts-list">
          {items.map(m => (
            <div key={m.id} className="comm-post-card comm-media-card">
              <div className="comm-post-top">
                <span className="comm-post-cat" style={{ color: '#4338ca', background: '#eef2ff' }}>{MEDIA_ICON[m.media_type]} {S.mediaTypeLabels[m.media_type]}</span>
                <span className="comm-post-city">{m.level}</span>
              </div>
              <div className="comm-post-title">{m.title}</div>
              <div className="comm-post-content">{m.description}</div>
              {m.link && <a href={m.link} target="_blank" rel="noopener noreferrer" className="comm-media-link">🔗 {m.link}</a>}
              <div className="comm-post-author">— {m.curated_by || m.students?.full_name || ''}</div>
            </div>
          ))}
        </div>
      )}

      {formOpen && <NewMediaModal session={session} S={S} onClose={() => setFormOpen(false)} />}
    </div>
  );
}

function NewMediaModal({ session, S, onClose }) {
  const [mediaType, setMediaType] = useState(MEDIA_TYPES[0]);
  const [level, setLevel] = useState(MEDIA_LEVELS[0]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [status, setStatus] = useState('');

  const submit = async () => {
    if (!title.trim() || !description.trim()) return;
    setStatus('sending');
    const { error } = await supabase.from('community_media').insert({
      student_id: session.id, media_type: mediaType, level, title: title.trim(),
      description: description.trim(), link: link.trim() || null,
    });
    setStatus(error ? 'error' : 'done');
  };

  return (
    <div className="modal-overlay" onClick={e => e.target.className === 'modal-overlay' && onClose()}>
      <div className="modal-box modal-wide comm-modal" style={{ textAlign: 'left' }}>
        <button className="modal-close" onClick={onClose}>✕</button>
        {status === 'done' ? (
          <div className="comm-modal-success">
            <div className="comm-modal-success-icon">✅</div>
            <p style={{ fontWeight: '700', color: '#16a34a', fontSize: '0.92rem', lineHeight: 1.5 }}>{S.submitted}</p>
            <button onClick={onClose} className="comm-modal-submit" style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: 'white' }}>{S.close}</button>
          </div>
        ) : (
          <>
            <div className="comm-modal-icon-header">
              <div className="comm-modal-icon" style={{ background: '#eef2ff' }}>🎬</div>
              <div className="comm-modal-title">{S.newMedia}</div>
            </div>

            <label className="comm-form-label">{S.mediaType}</label>
            <select className="comm-select comm-select-full" value={mediaType} onChange={e => setMediaType(e.target.value)}>
              {MEDIA_TYPES.map(t => <option key={t} value={t}>{MEDIA_ICON[t]} {S.mediaTypeLabels[t]}</option>)}
            </select>

            <label className="comm-form-label">{S.level}</label>
            <select className="comm-select comm-select-full" value={level} onChange={e => setLevel(e.target.value)}>
              {MEDIA_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>

            <label className="comm-form-label">{S.mediaTitle}</label>
            <input className="comm-input-full" placeholder={S.mediaTitlePh} value={title} onChange={e => setTitle(e.target.value)} maxLength={100} />

            <label className="comm-form-label">{S.mediaDesc}</label>
            <textarea className="comm-input-full comm-textarea" placeholder={S.mediaDescPh} value={description} onChange={e => setDescription(e.target.value)} maxLength={500} rows={4} />

            <label className="comm-form-label">{S.mediaLink}</label>
            <input className="comm-input-full" placeholder={S.mediaLinkPh} value={link} onChange={e => setLink(e.target.value)} maxLength={300} />

            {status === 'error' && <p style={{ color: '#dc2626', fontWeight: '700', fontSize: '0.85rem' }}>Error — try again.</p>}

            <button onClick={submit} disabled={!title.trim() || !description.trim() || status === 'sending'} className="comm-modal-submit" style={{ background: (title.trim() && description.trim()) ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#f1f5f9', color: (title.trim() && description.trim()) ? 'white' : '#94a3b8' }}>
              {status === 'sending' ? S.submitting : S.submit}
            </button>
            <button className="comm-modal-cancel" onClick={onClose}>{S.cancel}</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const communityStyles = `
  .comm-page { animation: commFadeIn 0.25s ease; }
  @keyframes commFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

  .comm-back { background: none; border: none; color: #64748b; font-weight: 700; cursor: pointer; margin-bottom: 14px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.9rem; padding: 0; transition: color 0.15s; }
  .comm-back:hover { color: #0f172a; }

  .comm-hero { display: flex; align-items: center; gap: 16px; background: linear-gradient(135deg,#0f766e,#0369a1 60%,#4338ca); border-radius: 22px; padding: 22px 24px; margin-bottom: 20px; box-shadow: 0 8px 24px rgba(15,118,110,0.25); position: relative; overflow: hidden; }
  .comm-hero::after { content: ''; position: absolute; top: -40px; right: -40px; width: 140px; height: 140px; border-radius: 50%; background: rgba(255,255,255,0.08); }
  .comm-hero-icon { font-size: 2.4rem; flex-shrink: 0; background: rgba(255,255,255,0.15); width: 60px; height: 60px; border-radius: 16px; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
  .comm-title { font-size: 1.35rem; font-weight: 900; margin: 0; color: white; letter-spacing: -0.3px; }
  .comm-hero-sub { margin: 4px 0 0; font-size: 0.82rem; color: rgba(255,255,255,0.85); font-weight: 600; }

  .comm-tabs { display: flex; gap: 6px; margin-bottom: 20px; background: #eef2f7; padding: 6px; border-radius: 16px; flex-wrap: wrap; }
  .comm-tab { flex: 1; min-width: 90px; padding: 11px 8px; border: none; background: none; border-radius: 12px; font-weight: 800; font-size: 0.78rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; color: #64748b; transition: all 0.18s; }
  .comm-tab:hover { color: #334155; }
  .comm-tab.active { background: white; color: #0f172a; box-shadow: 0 3px 10px rgba(15,23,42,0.1); }

  .comm-card { background: white; border-radius: 22px; box-shadow: 0 2px 14px rgba(15,23,42,0.06); overflow: hidden; }

  /* CHAT */
  .comm-chat-banner { display: flex; gap: 12px; align-items: flex-start; background: linear-gradient(135deg,#fff7ed,#fef3c7); border: 1.5px solid #fde68a; border-radius: 18px; padding: 14px 16px; margin-bottom: 16px; box-shadow: 0 2px 10px rgba(217,119,6,0.08); }
  .comm-chat-banner-icon { font-size: 1.3rem; flex-shrink: 0; line-height: 1.3; }
  .comm-chat-banner span:last-child { font-size: 0.82rem; font-weight: 650; color: #92400e; line-height: 1.5; }

  .comm-chat-card { display: flex; flex-direction: column; height: 62vh; background: linear-gradient(180deg,#fafbfd,white); }
  .comm-chat-scroll { flex: 1; overflow-y: auto; padding: 18px; display: flex; flex-direction: column; gap: 12px; }
  .comm-empty { text-align: center; color: #94a3b8; padding: 30px; font-weight: 600; }
  .comm-msg-row { display: flex; align-items: flex-end; gap: 8px; }
  .comm-msg-row.mine { justify-content: flex-end; }
  .comm-avatar { width: 30px; height: 30px; border-radius: 50%; color: white; font-size: 0.68rem; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 6px rgba(0,0,0,0.15); }
  .comm-msg-bubble { max-width: 72%; background: white; border: 1.5px solid #eef2f7; border-radius: 16px 16px 16px 4px; padding: 9px 13px; box-shadow: 0 2px 8px rgba(15,23,42,0.05); }
  .comm-msg-bubble.mine { background: linear-gradient(135deg,#16a34a,#15803d); color: white; border: none; border-radius: 16px 16px 4px 16px; box-shadow: 0 3px 10px rgba(22,163,74,0.3); }
  .comm-msg-name { font-size: 0.7rem; font-weight: 800; }
  .comm-msg-meta { display: flex; align-items: baseline; gap: 5px; margin-bottom: 3px; }
  .comm-msg-dot { font-size: 0.7rem; opacity: 0.5; }
  .comm-msg-text { font-size: 0.88rem; font-weight: 600; word-break: break-word; line-height: 1.4; }
  .comm-msg-time { font-size: 0.65rem; font-weight: 700; opacity: 0.65; }
  .comm-msg-bubble.mine .comm-msg-time { color: rgba(255,255,255,0.85); }
  .comm-chat-input-row { display: flex; gap: 10px; padding: 14px; border-top: 1px solid #f1f5f9; background: white; }
  .comm-chat-input { flex: 1; border: 1.5px solid #e2e8f0; border-radius: 50px; padding: 11px 18px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.88rem; transition: border-color 0.15s; }
  .comm-chat-input:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .comm-chat-send { background: linear-gradient(135deg,#16a34a,#15803d); color: white; border: none; border-radius: 50px; padding: 11px 22px; font-weight: 800; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; box-shadow: 0 3px 10px rgba(22,163,74,0.3); transition: transform 0.12s; }
  .comm-chat-send:active { transform: scale(0.96); }
  .comm-chat-send:disabled { background: #cbd5e1; cursor: default; box-shadow: none; }

  /* COHORT TOGGLE */
  .comm-cohort-toggle { display: flex; gap: 6px; margin-bottom: 12px; background: #eef2f7; padding: 5px; border-radius: 12px; }
  .comm-cohort-btn { flex: 1; border: none; background: none; padding: 8px; border-radius: 9px; font-weight: 800; font-size: 0.75rem; color: #64748b; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.15s; }
  .comm-cohort-btn.active { background: white; color: #0f172a; box-shadow: 0 2px 6px rgba(15,23,42,0.1); }

  /* STREAK BADGE IN CHAT */
  .comm-msg-streak { font-size: 0.68rem; font-weight: 800; color: #ea580c; }

  /* REACTIONS */
  .comm-reaction-row { display: flex; gap: 4px; margin-top: 4px; flex-wrap: wrap; }
  .comm-reaction-row.mine { justify-content: flex-end; }
  .comm-reaction-chip { background: white; border: 1.5px solid #eef2f7; border-radius: 50px; padding: 2px 8px; font-size: 0.78rem; cursor: pointer; display: flex; align-items: center; gap: 3px; transition: all 0.12s; font-family: 'Plus Jakarta Sans', sans-serif; }
  .comm-reaction-chip:hover { border-color: #cbd5e1; transform: translateY(-1px); }
  .comm-reaction-chip.active { background: #f0fdf4; border-color: #86efac; }
  .comm-reaction-count { font-size: 0.65rem; font-weight: 800; color: #475569; }

  /* WINS OF THE WEEK */
  .comm-post-card.comm-win-card { border-left-color: #d97706; background: linear-gradient(135deg,#fffbeb,white); }
  .comm-win-btn { background: linear-gradient(135deg,#d97706,#b45309) !important; box-shadow: 0 6px 18px rgba(217,119,6,0.3) !important; }

  /* SPEAKING PRACTICE CALLS */
  .comm-post-card.comm-call-card { border-left-color: #0f766e; }
  .comm-speaking-btn { background: linear-gradient(135deg,#0f766e,#0d9488) !important; box-shadow: 0 6px 18px rgba(15,118,110,0.3) !important; }
  .comm-call-join-btn { width: 100%; background: linear-gradient(135deg,#0f766e,#0d9488); color: white; border: none; border-radius: 12px; padding: 10px; font-weight: 800; font-size: 0.82rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; }
  .comm-call-matched-banner { background: #f0fdf4; color: #166534; border-radius: 10px; padding: 8px 12px; font-size: 0.78rem; font-weight: 700; }
  .comm-call-open-badge { background: #f1f5f9; color: #64748b; border-radius: 10px; padding: 8px 12px; font-size: 0.78rem; font-weight: 700; text-align: center; }

  /* RECOMMENDATIONS + MEDIA */
  .comm-guidelines { display: flex; align-items: center; gap: 8px; background: linear-gradient(135deg,#ecfdf5,#f0fdf4); color: #166534; padding: 12px 16px; border-radius: 14px; font-size: 0.8rem; font-weight: 700; margin-bottom: 16px; border: 1.5px solid #bbf7d0; }
  .comm-filters { display: flex; gap: 10px; margin-bottom: 16px; }
  .comm-select { flex: 1; border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 11px 14px; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 0.82rem; color: #0f172a; background: white; cursor: pointer; transition: border-color 0.15s; }
  .comm-select:focus { outline: none; border-color: #16a34a; }
  .comm-select-full { width: 100%; margin-bottom: 14px; }
  .comm-new-post-btn { width: 100%; background: linear-gradient(135deg,#16a34a,#15803d); color: white; border: none; border-radius: 16px; padding: 15px; font-weight: 800; font-size: 0.88rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; margin-bottom: 18px; box-shadow: 0 6px 18px rgba(22,163,74,0.3); transition: transform 0.12s, box-shadow 0.12s; }
  .comm-new-post-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(22,163,74,0.38); }
  .comm-new-post-btn:active { transform: scale(0.98); }

  .comm-empty-box { text-align: center; padding: 44px 20px; color: #64748b; background: white; border-radius: 20px; box-shadow: 0 2px 10px rgba(15,23,42,0.05); }
  .comm-posts-list { display: flex; flex-direction: column; gap: 14px; }
  .comm-post-card { background: white; border-radius: 18px; padding: 18px; box-shadow: 0 2px 12px rgba(15,23,42,0.06); border-left: 4px solid #16a34a; transition: transform 0.15s, box-shadow 0.15s; }
  .comm-post-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(15,23,42,0.1); }
  .comm-post-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 6px; }
  .comm-post-cat { font-size: 0.7rem; font-weight: 800; color: #16a34a; background: #f0fdf4; padding: 5px 12px; border-radius: 50px; }
  .comm-post-city { font-size: 0.72rem; font-weight: 800; color: #64748b; background: #f1f5f9; padding: 5px 12px; border-radius: 50px; }
  .comm-post-title { font-weight: 900; font-size: 0.98rem; color: #0f172a; margin-bottom: 5px; letter-spacing: -0.2px; }
  .comm-post-content { font-size: 0.85rem; color: #475569; line-height: 1.5; margin-bottom: 10px; }
  .comm-post-author { font-size: 0.72rem; color: #94a3b8; font-weight: 700; }
  .comm-media-link { display: block; font-size: 0.78rem; color: #0284c7; font-weight: 700; margin-bottom: 10px; word-break: break-all; text-decoration: none; }
  .comm-media-link:hover { text-decoration: underline; }

  .comm-form-label { display: block; font-size: 0.72rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; margin-top: 2px; }
  .comm-input-full { width: 100%; border: 1.5px solid #e2e8f0; border-radius: 14px; padding: 11px 15px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.88rem; margin-bottom: 14px; transition: border-color 0.15s; }
  .comm-input-full:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .comm-textarea { resize: vertical; }

  /* MODALS */
  .comm-page .modal-box.comm-modal { border-radius: 26px !important; padding: 30px !important; }
  .comm-modal-icon-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
  .comm-modal-icon { width: 46px; height: 46px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.35rem; flex-shrink: 0; }
  .comm-modal-title { font-weight: 900; color: #0f172a; font-size: 1.08rem; letter-spacing: -0.2px; }
  .comm-modal-submit { width: 100%; border: none; border-radius: 14px; padding: 14px; font-weight: 800; font-size: 0.9rem; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; margin-top: 4px; transition: transform 0.12s, box-shadow 0.12s; box-shadow: 0 6px 16px rgba(22,163,74,0.28); }
  .comm-modal-submit:active { transform: scale(0.98); }
  .comm-modal-submit:disabled { box-shadow: none; cursor: default; }
  .comm-modal-cancel { width: 100%; background: none; border: none; color: #94a3b8; font-weight: 700; padding: 12px; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 0.85rem; margin-top: 4px; transition: color 0.15s; }
  .comm-modal-cancel:hover { color: #475569; }
  .comm-modal-success { text-align: center; padding: 20px 0 4px; }
  .comm-modal-success-icon { font-size: 2.8rem; margin-bottom: 14px; }

  /* MEDIA CARD ACCENT */
  .comm-post-card.comm-media-card { border-left-color: #4338ca; }
  .comm-media-btn { background: linear-gradient(135deg,#4338ca,#3730a3) !important; box-shadow: 0 6px 18px rgba(67,56,202,0.3) !important; }

  @media (max-width: 480px) {
    .comm-hero { padding: 18px; gap: 12px; }
    .comm-hero-icon { width: 48px; height: 48px; font-size: 1.9rem; }
    .comm-title { font-size: 1.15rem; }
    .comm-msg-bubble { max-width: 82%; }
  }
`;
